import API from "./axios";

export const stockApi = {
  /** Unified cloth stock: bales + lumps grouped by (cloth + shade + location + rack) */
  getClothStock:    () => API.get("/stock/cloth"),

  // ── STOCK ADJUSTMENTS ───────────────────────────────────────────────
  createAdjustment: (body) => API.post("/stock/adjustments", body),

  // Reverses rather than deletes — the original stays in the ledger.
  reverseAdjustment: (id, reason) =>
    API.post(`/stock/adjustments/${id}/reverse`, { reason }),

  listAdjustments: ({ q, clothVariantId, page = 1, size = 25 } = {}) => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (clothVariantId) params.append("clothVariantId", clothVariantId);
    params.append("page", page);
    params.append("size", size);
    return API.get(`/stock/adjustments?${params.toString()}`);
  },

  /** Available cloth stock filtered by cloth + optional variant (on-demand for cutting issue).
   *  locationId (optional) — only that location's bales / lumps. */
  getClothStockByVariant: (clothId, clothVariantId, locationId) =>
    API.get(`/stock/cloth/by-variant?clothId=${clothId}${clothVariantId ? `&clothVariantId=${clothVariantId}` : ''}${locationId ? `&locationId=${locationId}` : ''}`),

  /** Material stock per (material + location + rack) from CONFIRMED GRNs */
  getMaterialStock: () => API.get("/stock/material"),

  // Phase 36bx — stock register exports.  search & location params
  // mirror the user's current filter on the page; backend returns the
  // file as binary (responseType:"blob") which the caller saves.
  exportClothExcel:    (search, location) => API.get("/stock/cloth/export/excel",
    { params: { search: search || "", location: location || "" }, responseType: "blob" }),
  exportClothPdf:      (search, location) => API.get("/stock/cloth/export/pdf",
    { params: { search: search || "", location: location || "" }, responseType: "blob" }),
  exportMaterialExcel: (search, location) => API.get("/stock/material/export/excel",
    { params: { search: search || "", location: location || "" }, responseType: "blob" }),
  exportMaterialPdf:   (search, location) => API.get("/stock/material/export/pdf",
    { params: { search: search || "", location: location || "" }, responseType: "blob" }),

  // Phase 36da.99.o — Stock Ledger: per-cloth date-wise IN/OUT/balance.
  // clothId required; clothVariantId optional (null = all variants);
  // fromDate / toDate ISO YYYY-MM-DD.
  // MATERIAL ledger — same response shape as the cloth one, so the page built
  // for cloth renders it unchanged.
  // EVERY MATERIAL for the range — the material equivalent of
  // getAllClothsSummary, which is what the cloth ledger page renders.
  getAllMaterialsSummary: (fromDate, toDate, search) =>
    API.get("/stock/material/ledger/all-materials-summary", {
      params: {
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
        ...(search ? { search } : {}),
      },
    }),

  // TKT-136 — the Material Stock report (Excel / PDF): per material, variant and
  // size - opening, Material GRN, Material Outward, other, current qty, rate, amount.
  materialStockReport: (format, fromDate, toDate, search) =>
    API.get(`/stock/material/ledger/report/${format === "PDF" ? "pdf" : "excel"}`, {
      params: {
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
        ...(search ? { search } : {}),
      },
      responseType: "blob",
    }),

  getMaterialLedger: (materialId, materialVariantId, fromDate, toDate) =>
    API.get("/stock/material/ledger", {
      params: {
        materialId,
        ...(materialVariantId ? { materialVariantId } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      },
    }),

  // POOL vs LEDGER per material. Returns ONLY the materials that disagree, so
  // an empty array is the healthy answer. No date range — it is a whole-history
  // check, which is the only version of the question worth asking.
  reconcileMaterialLedger: () => API.get("/stock/material/ledger/reconcile"),

  getClothLedger: (clothId, clothVariantId, fromDate, toDate) =>
    API.get("/stock/cloth/ledger", {
      params: {
        clothId,
        ...(clothVariantId ? { clothVariantId } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      },
    }),
  // Phase 36da.99.x — flat list of events across all cloths/variants in
  // the date range.  Used by the "All cloths" default mode on Stock Ledger.
  getAllClothEvents: (fromDate, toDate) =>
    API.get("/stock/cloth/ledger/all-events", {
      params: {
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      },
    }),
  // Phase 36da.99.cl2 — per-CLOTH-VARIANT Opening / IN / OUT / Balance for
  // the date range, with a free-text search across cloth name/code, variant
  // code, shade no and design no.  All totals recompute against the search.
  getAllClothsSummary: (fromDate, toDate, search) =>
    API.get("/stock/cloth/ledger/all-cloths-summary", {
      params: {
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
        ...(search && search.trim() ? { search: search.trim() } : {}),
      },
    }),
  // Phase 36da.99.cl2 — export the per-variant ledger exactly as shown on
  // screen (same search + date range).  Backend reuses the same query as the
  // table, so the file can never disagree with what the user is looking at.
  exportClothLedgerSummaryExcel: (fromDate, toDate, search) =>
    API.get("/stock/cloth/ledger/summary/export/excel", {
      params: {
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
        ...(search && search.trim() ? { search: search.trim() } : {}),
      },
      responseType: "blob",
    }),
  exportClothLedgerSummaryPdf: (fromDate, toDate, search) =>
    API.get("/stock/cloth/ledger/summary/export/pdf", {
      params: {
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
        ...(search && search.trim() ? { search: search.trim() } : {}),
      },
      responseType: "blob",
    }),
  exportClothLedgerExcel: (clothId, clothVariantId, fromDate, toDate) =>
    API.get("/stock/cloth/ledger/export/excel", {
      params: {
        clothId,
        ...(clothVariantId ? { clothVariantId } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      },
      responseType: "blob",
    }),
  exportClothLedgerPdf: (clothId, clothVariantId, fromDate, toDate) =>
    API.get("/stock/cloth/ledger/export/pdf", {
      params: {
        clothId,
        ...(clothVariantId ? { clothVariantId } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      },
      responseType: "blob",
    }),
};